import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { countryOptions } from '../../utils/countryFlags';

interface Competition {
  id: string;
  name: string;
  countryCode?: string;
  logoUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminCompetitions: React.FC = () => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState<Competition | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    countryCode: '',
    logoUrl: '',
    isActive: true
  });

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/competitions');
      setCompetitions(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch competitions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/admin/competitions', formData);
      setShowCreateModal(false);
      setFormData({ name: '', countryCode: '', logoUrl: '', isActive: true });
      fetchCompetitions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create competition');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCompetition) return;
    
    try {
      await apiClient.put(`/admin/competitions/${editingCompetition.id}`, formData);
      setEditingCompetition(null);
      setShowCreateModal(false);
      setFormData({ name: '', countryCode: '', logoUrl: '', isActive: true });
      fetchCompetitions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update competition');
    }
  };

  const handleDelete = async (competitionId: string) => {
    if (!window.confirm('Are you sure you want to delete this competition? This will also delete all related seasons and data.')) {
      return;
    }

    try {
      await apiClient.delete(`/admin/competitions/${competitionId}`);
      fetchCompetitions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete competition');
    }
  };

  const handleEdit = (competition: Competition) => {
    setEditingCompetition(competition);
    setFormData({
      name: competition.name,
      countryCode: competition.countryCode || '',
      logoUrl: competition.logoUrl || '',
      isActive: competition.isActive
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', countryCode: '', logoUrl: '', isActive: true });
    setEditingCompetition(null);
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading competitions...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Manage Football Competitions
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary px-4 py-2"
        >
          Create New Competition
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        {competitions.map((competition) => (
          <div key={competition.id} className="card p-6">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {competition.name}
                  </h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    competition.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {competition.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {competition.countryCode && (
                    <span className="mr-4">
                      🌍 {countryOptions.find(c => c.code === competition.countryCode)?.flag || '⚽'} {competition.countryCode}
                    </span>
                  )}
                  {competition.logoUrl && (
                    <span className="mr-4">🖼️ Logo: {competition.logoUrl}</span>
                  )}
                  <span>📅 Created: {new Date(competition.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(competition)}
                  className="btn-secondary px-3 py-1 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(competition.id)}
                  className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}

        {competitions.length === 0 && (
          <div className="text-center py-8">
            <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>
              No competitions found. Create your first competition!
            </p>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {editingCompetition ? 'Edit Competition' : 'Create New Competition'}
            </h2>
            
            <form onSubmit={editingCompetition ? handleUpdate : handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Competition Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                  required
                  placeholder="e.g., Premier League"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Country Code
                </label>
                <select
                  value={formData.countryCode}
                  onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                >
                  <option value="">Select a country code</option>
                  {countryOptions.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.name} ({country.code})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  Logo URL
                </label>
                <input
                  type="text"
                  value={formData.logoUrl}
                  onChange={(e) => setFormData({ ...formData, logoUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                  placeholder="e.g., /images/premier-league.png"
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                    Competition is active
                  </span>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingCompetition ? 'Update Competition' : 'Create Competition'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompetitions; 